import React, { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ApiService from "../services/ApiService";
import OAuthProviderButtons from "./OAuthProviderButtons";

const TERMS_URL = "https://orkaapps.com/links/terms";
const PRIVACY_URL = "https://orkaapps.com/links/privacy";

const LOGIN_MODE = { Password: "password", Otp: "otp" };
const OTP_STEP = { Request: "request", Verify: "verify" };

const loginIdSchema = Yup.string()
  .trim()
  .required("Email or mobile is required")
  .test(
    "mobile-or-email",
    "Enter a valid email address or 10 digit mobile number",
    (value) => {
      if (!value) return false;
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const mobileRegex = /^[0-9]{10}$/;
      return emailRegex.test(value) || mobileRegex.test(value);
    },
  );

const passwordSchema = Yup.object({
  username: loginIdSchema,
  password: Yup.string().required("Password is required"),
});

const otpSchema = Yup.object({
  username: loginIdSchema,
  verificationCode: Yup.string(),
});

const STATS = [
  { value: "3.2M+", label: "Events tracked daily" },
  { value: "98%", label: "Data accuracy" },
  { value: "12+", label: "Integrations" },
];

function BrandPanel() {
  return (
    <Box sx={{
      display: { xs: "none", md: "flex" },
      flexDirection: "column",
      justifyContent: "space-between",
      width: 460, flexShrink: 0,
      bgcolor: "#0F172A", p: 5,
      position: "relative", overflow: "hidden",
    }}>
      <Box sx={{ position: "absolute", top: -100, right: -100, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(91,91,214,0.3) 0%, transparent 70%)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", bottom: -80, left: -80, width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle, rgba(14,165,233,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, position: "relative" }}>
        <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(91,91,214,0.5)" }}>
          <BarChartOutlinedIcon sx={{ fontSize: 20, color: "#fff" }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#F1F5F9", lineHeight: 1.2 }}>OrkaAnalytics</Typography>
          <Typography sx={{ fontSize: "0.65rem", color: "#475569", letterSpacing: "0.04em" }}>Marketing platform</Typography>
        </Box>
      </Box>

      <Box sx={{ position: "relative" }}>
        <Typography sx={{ fontSize: "2rem", fontWeight: 800, color: "#F1F5F9", lineHeight: 1.2, letterSpacing: "-0.03em", mb: 2 }}>
          All your marketing data,{" "}
          <Box component="span" sx={{ background: "linear-gradient(90deg, #818CF8 0%, #38BDF8 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            in one place.
          </Box>
        </Typography>
        <Typography sx={{ fontSize: "0.875rem", color: "#64748B", lineHeight: 1.75 }}>
          Connect Facebook, Instagram, Google Analytics and more. Unified insights without switching tabs.
        </Typography>
        <Box sx={{ display: "flex", gap: 4, mt: 4 }}>
          {STATS.map((s) => (
            <Box key={s.label}>
              <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: "#F1F5F9", lineHeight: 1 }}>{s.value}</Typography>
              <Typography sx={{ fontSize: "0.72rem", color: "#475569", mt: 0.5 }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ bgcolor: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 3, p: 3 }}>
        <Typography sx={{ fontSize: "0.875rem", color: "#94A3B8", lineHeight: 1.75, fontStyle: "italic" }}>
          &quot;OrkaAnalytics cut our reporting time in half. We finally have a single source of truth across all ad platforms.&quot;
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#fff" }}>SR</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#E2E8F0" }}>Sarah R.</Typography>
            <Typography sx={{ fontSize: "0.7rem", color: "#475569" }}>Head of Growth, Lumio</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function OtpDigitInputs({ value, onChange }) {
  const digits = (value + "    ").slice(0, 4).split("");

  function updateDigit(index, digit) {
    const code = [...digits];
    code[index] = digit;
    onChange(code.join("").replace(/\s/g, ""));
  }

  return (
    <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", my: 2 }}>
      {Array.from({ length: 4 }).map((_, idx) => (
        <TextField
          key={idx}
          inputProps={{
            maxLength: 1,
            inputMode: "numeric",
            style: { textAlign: "center", fontSize: "1.25rem", fontWeight: 700 },
          }}
          sx={{ width: 52 }}
          value={digits[idx]?.trim() || ""}
          onChange={(e) => {
            const d = e.target.value.replace(/\D/g, "").slice(-1);
            updateDigit(idx, d);
            if (d && e.target.nextSibling) {
              const next = e.target.parentElement?.parentElement?.nextSibling?.querySelector("input");
              next?.focus();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace") {
              if (digits[idx]?.trim()) {
                e.preventDefault();
                updateDigit(idx, "");
                return;
              }
              const prev = e.target.parentElement?.parentElement?.previousSibling?.querySelector("input");
              if (prev) {
                e.preventDefault();
                updateDigit(idx - 1, "");
                prev.focus();
              }
            }
          }}
        />
      ))}
    </Box>
  );
}

export function SignInPage() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMode, setLoginMode] = useState(LOGIN_MODE.Password);
  const [otpStep, setOtpStep] = useState(OTP_STEP.Request);
  const [verificationId, setVerificationId] = useState(null);

  useEffect(() => {
    document.title = "Sign in · Orka Analytics";
  }, []);

  function handleModeChange(_e, mode) {
    setLoginMode(mode);
    setOtpStep(OTP_STEP.Request);
    setVerificationId(null);
    setError(null);
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", width: "100%", boxSizing: "border-box", overflowX: "hidden" }}>
      <BrandPanel />

      <Box sx={{
        flex: 1, minWidth: 0, width: "100%", boxSizing: "border-box",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        bgcolor: "#F0F2F7", px: { xs: 2, sm: 5 }, py: { xs: 3, sm: 5 },
      }}>
        <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.5, mb: 4 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: "9px", background: "linear-gradient(135deg, #818CF8 0%, #5B5BD6 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <BarChartOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A" }}>OrkaAnalytics</Typography>
        </Box>

        <Box sx={{
          bgcolor: "#fff", borderRadius: 3, border: "1px solid #E8EDF5",
          px: { xs: 2, sm: 4.5 }, py: { xs: 3, sm: 4.5 },
          width: "100%", maxWidth: 420, minWidth: 0, boxSizing: "border-box",
          boxShadow: "0px 4px 24px rgba(15,23,42,0.07)",
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#0F172A", mb: 0.75 }}>Sign in</Typography>
          <Typography variant="body2" sx={{ color: "#64748B", mb: 2 }}>
            Sign in with your email or mobile and password.
          </Typography>

          <Tabs
            value={loginMode}
            onChange={handleModeChange}
            variant="fullWidth"
            sx={{ mb: 2, minHeight: 40, "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 40 } }}
          >
            <Tab label="Password" value={LOGIN_MODE.Password} />
            <Tab label="OTP" value={LOGIN_MODE.Otp} />
          </Tabs>

          {error && <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>{error}</Alert>}

          {loginMode === LOGIN_MODE.Password ? (
            <Formik
              initialValues={{ username: "", password: "" }}
              validationSchema={passwordSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setError(null);
                try {
                  await ApiService.signin(values.username.trim(), values.password);
                  navigate("/", { replace: true });
                } catch (e) {
                  setError(e?.response?.data?.message || e?.message || "Sign in failed. Try again.");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                <Form noValidate>
                  <TextField fullWidth name="username" label="Email or mobile" margin="normal"
                    value={values.username} onChange={handleChange} onBlur={handleBlur}
                    error={touched.username && Boolean(errors.username)} helperText={touched.username && errors.username}
                    autoComplete="username" autoFocus />
                  <TextField fullWidth name="password" label="Password" type={showPassword ? "text" : "password"} margin="normal"
                    value={values.password} onChange={handleChange} onBlur={handleBlur}
                    error={touched.password && Boolean(errors.password)} helperText={touched.password && errors.password}
                    autoComplete="current-password"
                    InputProps={{ endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((v) => !v)} edge="end" tabIndex={-1}>
                          {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    )}} />
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.75, mb: 3 }}>
                    <Link component={RouterLink} to="/forgot-password" sx={{ fontSize: "0.8125rem", color: "#5B5BD6", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                      Forgot password?
                    </Link>
                  </Box>
                  <Button fullWidth type="submit" variant="contained" size="large" disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ py: 1.25, fontSize: "0.9375rem" }}>
                    {isSubmitting ? "Signing in…" : "Sign in"}
                  </Button>
                </Form>
              )}
            </Formik>
          ) : (
            <Formik
              initialValues={{ username: "", verificationCode: "" }}
              validationSchema={otpSchema}
              onSubmit={async (values, { setSubmitting }) => {
                setError(null);
                try {
                  if (otpStep === OTP_STEP.Request) {
                    const id = await ApiService.sendLoginOtp(values.username);
                    if (id?.mobileVerificationId) {
                      setVerificationId(id.mobileVerificationId);
                    } else if (id?.emailVerificationId) {
                      setVerificationId(id.emailVerificationId);
                    } else if (typeof id === "string") {
                      setVerificationId(id);
                    } else {
                      throw new Error("Unexpected OTP response");
                    }
                    setOtpStep(OTP_STEP.Verify);
                    return;
                  }
                  if (!values.verificationCode || values.verificationCode.trim().length < 4) {
                    setError("Enter the 4-digit OTP");
                    return;
                  }
                  await ApiService.signInWithOtp({
                    loginId: values.username.trim(),
                    verificationId,
                    verificationCode: values.verificationCode.trim(),
                  });
                  navigate("/", { replace: true });
                } catch (e) {
                  setError(e?.message || "OTP sign-in failed. Try again.");
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting }) => (
                <Form noValidate>
                  {otpStep === OTP_STEP.Request ? (
                    <>
                      <TextField fullWidth name="username" label="Email or mobile" margin="normal"
                        value={values.username} onChange={handleChange} onBlur={handleBlur}
                        error={touched.username && Boolean(errors.username)} helperText={touched.username && errors.username}
                        autoComplete="username" autoFocus />
                      <Button fullWidth type="submit" variant="contained" size="large" disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ mt: 3, py: 1.25, fontSize: "0.9375rem" }}>
                        {isSubmitting ? "Sending…" : "Send OTP"}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: "#64748B", mb: 1 }}>
                        Enter the code sent to <strong>{values.username}</strong>
                        {" · "}
                        <Link
                          component="button"
                          type="button"
                          onClick={() => {
                            setOtpStep(OTP_STEP.Request);
                            setVerificationId(null);
                            setFieldValue("verificationCode", "");
                          }}
                          sx={{ color: "#5B5BD6", fontSize: "0.8125rem" }}
                        >
                          Change
                        </Link>
                      </Typography>
                      <OtpDigitInputs
                        value={values.verificationCode}
                        onChange={(code) => setFieldValue("verificationCode", code)}
                      />
                      <Button fullWidth type="submit" variant="contained" size="large" disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ mt: 2, py: 1.25, fontSize: "0.9375rem" }}>
                        {isSubmitting ? "Verifying…" : "Verify & sign in"}
                      </Button>
                    </>
                  )}
                </Form>
              )}
            </Formik>
          )}

          {loginMode === LOGIN_MODE.Password && (
            <OAuthProviderButtons
              onAuthenticated={() => navigate("/", { replace: true })}
              onError={(message) => setError(message)}
            />
          )}

          <Typography variant="caption" sx={{ color: "#94A3B8", mt: 2.5, display: "block", textAlign: "center", lineHeight: 1.6 }}>
            By continuing, you agree to our{" "}
            <Link href={TERMS_URL} target="_blank" rel="noopener noreferrer" sx={{ color: "#5B5BD6" }}>
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" sx={{ color: "#5B5BD6" }}>
              Privacy Policy
            </Link>
            .
          </Typography>

          <Typography variant="body2" sx={{ color: "#64748B", mt: 2.5, textAlign: "center" }}>
            New to Orka Analytics?{" "}
            <Link component={RouterLink} to="/signup" sx={{ color: "#5B5BD6", fontWeight: 600, textDecoration: "none" }}>
              Create account
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
